import {
  Box,
  Button,
  Flex,
  HStack,
  Link,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {ArticleBlock} from '@/domains/panier/PanierFound/ArticleBlock';
import isEqual from 'react-fast-compare';
import {
  HaliRenderOne,
  useHalCollection,
  useHalState,
  useHalStates,
} from '@synako/haligator';
import {Navigable, Problem, State} from '@synako/halitrek';
import {
  ItemCatalogue2StateFF,
  isItemStateCatalogueFF,
  usePanier,
} from 'ff-common';
import {useBuyingBlocks} from '@/hooks/panier/useBuyingBlocks';
import {useAjouterAvecPrixSaisi} from '@/hooks/panier/useAjouterAvecPrixSaisi';
import {useAjouterAuPanierDepuisModale} from '@/hooks/panier/useAjouterAuPanierDepuisModale';
import {
  ArticleDesc,
  ItemCatalogue2Desc,
  ListeDesc,
} from 'api-types/ffconnect-hal';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {LigneProduitCommune} from '@/domains/ligneProduit/LigneProduitCommune';
import {SectionHeader} from '@/domains/ligneProduit/SectionHeader';
import {Trans, useTranslation} from 'react-i18next';
import {
  useApi,
  useCadencier,
  useClient,
  useListes,
} from '../../../../haligator-factory';
import {LigneProduitLayout} from '../LigneProduitLayout';
import {CheckIcon} from '@chakra-ui/icons';
import {SwitchIcon} from '@/components/svg/SwitchIcon';
import {CadencierIcon} from '@/components/svg/CadencierIcon';
import {ListIcon} from '@/components/svg/ListIcon';
import {AjoutListeBouton} from '@/domains/cadencier/components/AjoutListeBouton';
import NiceModal from '@ebay/nice-modal-react';
import {ModaleRemplacementCatalogue} from '@/domains/panier/components/ModaleRemplacementCatalogue';
import {Destination, useListItems} from '../../context/ListItemsContext';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {Periode} from '../ProduitsParSaison';
import {PrixBlockUnitaire} from '@/domains/cadencier/components/PrixBlockUnitaire';
import {usePrixCategorieTag} from '@/hooks/usePrixCategorieTag';
import {useEditionPrixState} from '@/hooks/panier/usePriceEditor';
import {HistoriqueAchatButton} from '@/domains/ligneProduit/HistoriqueAchatButton';
import {ArticleStatistiques} from 'api-types/tolede';
import {TriSelection} from 'api-types/ffconnect';
import {useNavigate} from 'react-router-dom';
import {routes, toDecimalFr} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {DotLoaderSlow} from '@/components/Loading/DotLoaderSlow';
import {ListItemsProvider} from '../../context/ListItemsContext';

/**
 * Composant pour une section de produits saisonniers avec en-tête pliable
 */
interface LigneProduitsSaisonniersSection {
  libelle_web: string;
  categorie_id: string;
  articles_tri: {code: string; dans_catalogue?: boolean}[];
  statItems: ArticleStatistiques[];
  selectedListe: Navigable<ListeDesc> | undefined;
  refreshing: boolean;
}

const LigneProduitsSaisonniersSection =
  React.memo<LigneProduitsSaisonniersSection>(
    ({libelle_web, categorie_id, articles_tri, statItems, refreshing}) => {
      const {isOpen, onToggle} = useDisclosure();

      const productItems = useMemo(() => {
        return articles_tri.flatMap((produit) => {
          // Find matching items
          const matchingStats = statItems.filter(
            (stat) => stat.code_article === produit.code,
          );

          if (matchingStats.length) {
            return matchingStats.map((stat) => (
              <LigneProduitSaisonnier
                key={`${produit.code}-${stat.code_article}`}
                articleStat={stat}
                dansCatalogue={produit.dans_catalogue}
                refreshing={refreshing}
              />
            ));
          }

          // Placeholder si pas de correspondance
          return [
            <LigneProduitLayout
              key={produit.code}
              precommande={false}
              px='0'
            >
              <Skeleton
                height='50px'
                w='100%'
              />
            </LigneProduitLayout>,
          ];
        });
      }, [articles_tri, statItems, refreshing]);

      return (
        <SectionHeader
          title={libelle_web}
          count={articles_tri.length}
          id={categorie_id}
          isOpen={isOpen}
          onToggle={onToggle}
        >
          <Box
            marginBottom='1px'
            px='12px'
          >
            {productItems}
          </Box>
        </SectionHeader>
      );
    },
    (prevProps, nextProps) => {
      // Check if the data that would cause expensive recomputation has changed
      return (
        isEqual(prevProps.articles_tri, nextProps.articles_tri) &&
        isEqual(prevProps.statItems, nextProps.statItems) &&
        prevProps.refreshing === nextProps.refreshing
      );
    },
  );

/**
 * Composant principal pour l'affichage des lists saisonnières
 */
export const ProduitsSaisonniers: React.FC<{
  selectedPeriod: Periode;
  selectedListe: Navigable<ListeDesc> | undefined;
  destination: Destination; // Indique la destination d'ajout des produits (panier, cadencier ou liste)
  setItemCount: (count: number) => void;
  setSomethingRefreshing: (refreshing: boolean) => void;
}> = ({
  selectedPeriod,
  selectedListe,
  destination,
  setItemCount,
  setSomethingRefreshing,
}) => {
  const {t} = useTranslation();
  const listesHook = useHalCollection(useListes());

  const cadencierHook = useHalState(useCadencier());
  const {state: selectedListeState} = useHalState(selectedListe);
  const isCadencier = selectedListe === undefined;

  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();

  const liste_saisonniere = useHalState(
    useClient().follow('commande_statistiques_tri', {
      debut: selectedPeriod.date_debut,
      fin: selectedPeriod.date_fin,
    }),
  );

  useEffect(() => {
    if (liste_saisonniere.data)
      setItemCount(liste_saisonniere.data.nombre_articles ?? 0);
  }, [liste_saisonniere.data, setItemCount]);

  useEffect(() => {
    setSomethingRefreshing(
      liste_saisonniere.loading || liste_saisonniere.refreshing,
    );
  }, [
    setSomethingRefreshing,
    liste_saisonniere.refreshing,
    liste_saisonniere.loading,
  ]);

  const {state: statState, loading: statLoading} = useHalState(
    liste_saisonniere.navigable.follow('commande_statistiques'),
  );
  // Lignes du panier remontées ici pour éviter N appels identiques dans chaque ligne produit
  const {states: lignesDePanier} = useHalStates(
    usePanier().followAll('lignes_de_panier'),
  );
  // Utiliser la première liste par défaut si on n'a pas sélectionné de liste
  const cadencierItems = useHalStates(
    cadencierHook.navigable.followAll('articles'),
  );
  const selectedListeItems = useHalStates(
    selectedListe?.followAll('selection'),
  );

  // Référence pour suivre la dernière liste valide
  const lastValidProduitsListeRef = useRef<State<ArticleDesc>[] | null>(null);
  const {states: produits_liste} = useMemo(() => {
    const currentValue = isCadencier ? cadencierItems : selectedListeItems;

    if (currentValue.states) {
      lastValidProduitsListeRef.current = currentValue.states;
    }

    if (
      listesHook.refreshing &&
      !currentValue.states &&
      lastValidProduitsListeRef.current
    ) {
      return {states: lastValidProduitsListeRef.current};
    }

    return currentValue;
  }, [isCadencier, cadencierItems, selectedListeItems, listesHook.refreshing]);

  // Si aucun mois sélectionné ou "tout" sélectionné, afficher tous les produits
  // const isAllMonths = selectedPeriod.date_debut === undefined && selectedPeriod.date_fin === undefined;

  // Fonction pour ajouter à la liste
  const handleAddToList = useCallback(
    async (
      item_catalogue: State<ItemCatalogue2Desc>,
      setIsAdding: (adding: boolean) => void,
    ) => {
      if (!isCadencier && !selectedListeState) {
        textToast(t('liste.aucune_liste'));
        return;
      }

      // Utiliser la liste sélectionnée

      setIsAdding(true);
      try {
        // Appel à l'API pour ajouter l'article à la liste
        if (isCadencier) {
          if (!isItemStateCatalogueFF(item_catalogue)) return;
          await item_catalogue.submitCommand('c:ajouter-au-cadencier', {
            code_article: item_catalogue.data.code,
          });
          // Afficher un message de succès
          textToast([
            <Trans
              key='toast-text'
              t={t}
            >
              {t('toast.ajout_cadencier')}
            </Trans>,
            <Link
              key='toast-button'
              fontWeight='bold'
              textDecorationLine='underline'
              onClick={() => {
                navigate(buildClientRoute(routes.client.cadencier));
              }}
            >
              {t('erreur404.voir_cadencier')}
            </Link>,
          ]);
        } else {
          if (!selectedListeState) {
            textToast(t('liste.aucune_liste'));
            return;
          }
          if (!isItemStateCatalogueFF(item_catalogue)) return;
          await selectedListe.submitCommand('c:ajouter-article-a-une-liste', {
            liste_id: selectedListeState.data.id,
            code_article: item_catalogue.data.code,
          });

          // Afficher un message de succès
          const code_liste = selectedListeState.data.code_liste;
          textToast([
            <Trans
              key='toast-text'
              t={t}
            >
              {t('liste.produit_ajoute', {
                nom: selectedListeState.data.nom_liste,
              })}
            </Trans>,
            <Link
              key='toast-button'
              fontWeight='bold'
              textDecorationLine='underline'
              onClick={() => {
                navigate(buildClientRoute(routes.client.listes, {code_liste}));
              }}
            >
              {t('toast.voir_liste')}
            </Link>,
          ]);
        }
      } catch (error) {
        onError(error as Problem);
      } finally {
        setIsAdding(false);
      }
    },
    [
      isCadencier,
      selectedListeState,
      textToast,
      t,
      navigate,
      buildClientRoute,
      selectedListe,
      onError,
    ],
  );

  if (statLoading) {
    return (
      <HStack
        w='100%'
        justifyContent='center'
      >
        <DotLoaderSlow />
      </HStack>
    );
  }
  if (
    statState?.data.par_article.length === 0 ||
    !liste_saisonniere.data?.tri_selection
  ) {
    return (
      <>
        <Box
          p={5}
          textAlign='center'
          bg='white'
        >
          <Text>{t('liste.aucun_produit')}</Text>
        </Box>
      </>
    );
  }

  // Filtrer par mois si nécessaire (à implémenter selon la logique métier)
  const tri_selection_produits: TriSelection =
    liste_saisonniere.data.tri_selection;

  return (
    <ListItemsProvider
      produits_liste={produits_liste}
      destination={destination}
      lignesDePanier={lignesDePanier}
      handleAddToList={
        destination !== 'panier' &&
        (isCadencier ? !!cadencierHook.state : !!selectedListeState)
          ? handleAddToList
          : undefined
      }
    >
      <VStack
        alignItems='stretch'
        width='100%'
        spacing={4}
      >
        <VStack
          alignItems='stretch'
          width='100%'
          spacing={4}
        >
          {tri_selection_produits.map(
            ({libelle_web, categorie_id, articles: articles_tri}) => (
              <LigneProduitsSaisonniersSection
                key={categorie_id}
                libelle_web={libelle_web}
                categorie_id={categorie_id}
                articles_tri={articles_tri}
                statItems={statState?.data.par_article ?? []}
                selectedListe={selectedListe}
                refreshing={listesHook.refreshing}
              />
            ),
          )}
        </VStack>
      </VStack>
    </ListItemsProvider>
  );
};

/**
 * Charge la ligne de produit
 */
interface LigneProduitSaisonnierProps {
  articleStat: ArticleStatistiques;
  dansCatalogue?: boolean;
  refreshing?: boolean;
}

export const LigneProduitSaisonnier: React.FC<LigneProduitSaisonnierProps> =
  React.memo(({articleStat, dansCatalogue, refreshing}) => {
    const {t} = useTranslation();
    const code_article = articleStat.code_article;
    const articleNav = useApi().follow('get-article', {code_article});

    return (
      <HaliRenderOne
        nav={articleNav}
        fallback={() => {
          //L'article est normalement toujours présent dans la base article
          //(puisqu'il est dans le tri)
          return (
            <ProduitSaisonnierItemFallback
              articleStat={articleStat}
              message={t('error.article_introuvable')}
              refreshing={refreshing}
            />
          );
        }}
        render={({state: articleState}) => {
          // Si le serveur indique que l'article n'est pas au catalogue, afficher le fallback directement
          if (dansCatalogue === false) {
            return (
              <ProduitSaisonnierItemFallback
                articleStat={articleStat}
                articleState={articleState}
                message={t('error.item_catalogue_introuvable')}
                refreshing={refreshing}
              />
            );
          }
          return (
            <LigneProduitSaisonnierCatalogue
              articleStat={articleStat}
              articleState={articleState}
              refreshing={refreshing}
            />
          );
        }}
      />
    );
  });

/**
 * Sous-composant monté uniquement quand l'article est au catalogue,
 * pour éviter de déclencher l'appel get-item_catalogue_2 inutilement
 */
const LigneProduitSaisonnierCatalogue: React.FC<{
  articleStat: ArticleStatistiques;
  articleState: State<ArticleDesc>;
  refreshing?: boolean;
}> = ({articleStat, articleState, refreshing}) => {
  const {t} = useTranslation();
  const client = useClient();
  const itemNav = client.follow('go:item_catalogue_2', {
    marketplace_id: 'FF',
    code: articleStat.code_article,
  });

  return (
    <HaliRenderOne
      nav={itemNav}
      fallback={() => {
        return (
          <ProduitSaisonnierItemFallback
            articleStat={articleStat}
            articleState={articleState}
            message={t('error.item_catalogue_introuvable')}
            refreshing={refreshing}
          />
        );
      }}
      render={({state: itemState}) => {
        return (
          <ProduitSaisonnierItem
            itemState={itemState}
            articleState={articleState}
            articleStat={articleStat}
            refreshing={refreshing}
          />
        );
      }}
    />
  );
};

export const ProduitSaisonnierItemFallback: React.FC<{
  articleStat: ArticleStatistiques;
  articleState?: State<ArticleDesc>;
  itemState?: State<ItemCatalogue2Desc>;
  message?: string;
  refreshing?: boolean;
}> = ({articleStat, articleState, message}) => {
  const {code_article, libelle} = articleStat;
  const client = useClient();
  const {t} = useTranslation();
  const {isItemInList, handleAddToList, destination, lignesDePanier} =
    useListItems();
  const [isAdding, setIsAdding] = useState(false);
  const ajouterAuPanier = useAjouterAuPanierDepuisModale();

  // Vérifier si l'article est déjà dans le panier (mode panier)
  const isArticleInPanier = (code: string) =>
    !!lignesDePanier?.find((l) => l.data.code_article === code);

  // Fonction pour ouvrir la modale de recherche d'alternatives
  const onFindAlternative = () => {
    // Création d'un objet article minimal pour la modale
    if (!articleState) return;
    const articleData = articleState.data;

    // Callback d'ajout selon la destination
    const onListAdd =
      destination === 'panier'
        ? // En mode panier : le hook mutualisé gère tout (quantité, toast, contrôle de
          // disponibilité, erreurs).
          ajouterAuPanier
        : handleAddToList
          ? // En mode cadencier/liste : ajouter à la liste/cadencier
            async (code_article: string) => {
              // Récupérer l'item catalogue pour le code article sélectionné
              const navItem = client.follow('go:item_catalogue_2', {
                marketplace_id: 'FF',
                code: code_article,
              });
              const itemState = await navItem.get();

              // Utiliser la fonction ajouter_liste existante
              handleAddToList(itemState, setIsAdding);
            }
          : undefined;

    // Afficher la modale
    NiceModal.show(ModaleRemplacementCatalogue, {
      articleData,
      mode:
        destination === 'panier'
          ? 'add-to-panier'
          : destination === 'cadencier'
            ? 'add-to-cadencier'
            : 'add-to-list',
      onListAdd,
      isDisabled: destination === 'panier' ? isArticleInPanier : isItemInList,
    });
  };

  return (
    <LigneProduitLayout
      precommande={false}
      px='0'
    >
      <Flex
        direction={{base: 'column', md: 'row'}}
        width='100%'
      >
        <Box flex={1}>
          <ArticleBlock
            code_article={code_article}
            libelleFallback={libelle}
            skipItemCatalogue
            libelles_tronques
          />
        </Box>
        <HStack
          ml='auto'
          flex={1}
          spacing={3}
          align={{base: 'stretch', md: 'center'}}
        >
          <Text
            mx='auto'
            textAlign={'center'}
          >
            {message}
          </Text>

          {articleState && (
            <Button
              leftIcon={
                isAdding ? <Spinner /> : <SwitchIcon color='blue.main' />
              }
              variant='outline'
              colorScheme='blue'
              _hover={{bg: 'blue.main_transparency_30'}}
              w={{base: '100%', md: '13em'}}
              onClick={onFindAlternative}
            >
              {isAdding ? '' : t('liste.trouver_alternative')}
            </Button>
          )}
        </HStack>
      </Flex>
    </LigneProduitLayout>
  );
};

// Affiche un produit
interface ProduitSaisonnierItemProps {
  itemState: State<ItemCatalogue2Desc>;
  articleState: State<ArticleDesc>;
  articleStat: ArticleStatistiques;
  refreshing?: boolean;
}

const ProduitSaisonnierItem: React.FC<ProduitSaisonnierItemProps> = React.memo(
  (props) => {
    const {itemState, ...rest} = props;
    // Pour le typage
    if (!isItemStateCatalogueFF(itemState)) return null;
    return (
      <ProduitSaisonnierItemContent
        {...rest}
        itemState={itemState}
      />
    );
  },
);

const ProduitSaisonnierItemContent: React.FC<{
  itemState: ItemCatalogue2StateFF;
  articleState: State<ArticleDesc>;
  articleStat: ArticleStatistiques;
  refreshing?: boolean;
}> = React.memo(({itemState, articleState, articleStat, refreshing}) => {
  const {t} = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  // État persistant pour suivre si l'article a été récemment ajouté (pendant le rafraîchissement)
  const [wasRecentlyAdded, setWasRecentlyAdded] = useState(false);
  // Contexte partagé par ListItemsProvider :
  // - isItemInList : vérifie si l'article est déjà dans la liste/cadencier sélectionné
  // - handleAddToList : fonction d'ajout à la liste/cadencier (undefined en mode panier)
  // - destination : détermine le rendu ('panier' → contrôles ajout panier, 'cadencier'/'liste' → bouton ajout liste)
  const {isItemInList, handleAddToList, destination} = useListItems();
  const dansLaListe = isItemInList(articleState.data.tolede.code_article);

  // Maintenir l'état pendant le rafraîchissement
  React.useEffect(() => {
    if (isAdding) {
      // Quand on commence à ajouter, marquer comme récemment ajouté
      setWasRecentlyAdded(true);
    } else if (!refreshing && wasRecentlyAdded && dansLaListe) {
      // Réinitialiser seulement quand le rafraîchissement est terminé ET
      // que le serveur a confirmé que l'item est dans la liste
      setWasRecentlyAdded(false);
    }
  }, [isAdding, refreshing, wasRecentlyAdded, dansLaListe]);

  // Déterminer si l'article est dans la liste ou a été récemment ajouté
  const isInList = dansLaListe || wasRecentlyAdded;

  // Récupération des données du produit
  const {stats_par_livraison, unite_de_commande_client} = articleStat;
  const quantite_totale = stats_par_livraison.reduce((acc, stat) => {
    acc += stat.quantite;
    return acc;
  }, 0);
  const quantite_format = toDecimalFr(quantite_totale, 0, 2);

  // Récupération des données du prix
  const prixHT = itemState.data.prix_unitaire_ht;
  const prixTtc = itemState.data.prix_unitaire_ttc;
  const tauxTVA = itemState.data.tva;

  // Composant pour le prix
  const prixBlock = (
    <PrixBlockUnitaire
      prixUnitaire={prixHT}
      prixUnitaireTtc={prixTtc}
      uniteFacturation={itemState.data.unite_de_facturation}
      tauxTVA={tauxTVA}
      style={{h: '100%', mt: {base: '0', md: '15px'}}}
    />
  );

  if (destination === 'panier') {
    return (
      <ProduitSaisonnierItemPanier
        itemState={itemState}
        articleState={articleState}
        prixHT={prixHT}
        prixTtc={prixTtc}
        tauxTVA={tauxTVA}
      />
    );
  }

  // Bouton d'ajout à la liste — "Ajouter au cadencier / à la liste" + bouton Ajout Liste (comme en mode panier)
  const ajoutButton = (
    <HStack
      gap='1'
      w={{base: '100%', md: 'auto'}}
    >
      {/* Bouton d'ajout à la liste */}
      <Tooltip
        w={{base: '100%', md: 'auto'}}
        isDisabled={!(!handleAddToList || (dansLaListe && !isAdding))}
        label={
          !handleAddToList
            ? t('liste.selectionner_liste_tooltip')
            : t('liste.produit_deja_dans_liste')
        }
        placement='top'
        hasArrow
      >
        <Button
          leftIcon={
            isInList ? (
              <CheckIcon />
            ) : destination === 'cadencier' ? (
              <CadencierIcon
                height='5'
                width='5'
                color='currentColor'
              />
            ) : (
              <ListIcon
                color='currentColor'
                width='16px'
              />
            )
          }
          variant='outline'
          colorScheme={handleAddToList ? 'blue' : 'gray'}
          _hover={{bg: 'blue.main_transparency_30'}}
          w={{base: '100%', md: '13em'}}
          h='35px'
          minH='35px'
          isLoading={isAdding}
          onClick={() => {
            handleAddToList?.(itemState, setIsAdding);
          }}
          //Disable si déjà dans la liste
          disabled={isAdding || isInList || !handleAddToList}
        >
          {isInList
            ? destination === 'cadencier'
              ? t('liste.dans_le_cadencier')
              : t('liste.dans_la_liste')
            : destination === 'cadencier'
              ? t('liste.ajouter_au_cadencier')
              : t('liste.ajouter_a_la_liste')}
        </Button>
      </Tooltip>
      <AjoutListeBouton itemState={itemState} />
    </HStack>
  );

  // Information de quantité — Quantité saisonnière (tooltip + encadré gris) — à la place du selectBlock dans LigneProduitCommune
  const quantiteSaisonniereBlock = unite_de_commande_client && (
    <Tooltip
      label={t('liste.quantite_saisonniere_tooltip')}
      placement='top'
      hasArrow
    >
      <VStack
        bg='gray.100'
        borderRadius='md'
        h='35px'
        minH='35px'
        w='110px'
        minW='110px'
        px='3'
        justifyContent='center'
        alignItems='center'
      >
        <Text
          fontSize='sm'
          color='gray.700'
          whiteSpace='nowrap'
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {quantite_format}&nbsp;
          {t(`unite.${unite_de_commande_client}`, {count: quantite_totale})}
        </Text>
      </VStack>
    </Tooltip>
  );

  // Mode cadencier / liste : même structure que LigneProduitCommune (prix à gauche, quantité à droite du prix, bouton à droite)
  return (
    <LigneProduitLayout
      precommande={false}
      px='0'
    >
      {/* Desktop */}
      <VStack
        w='100%'
        position='relative'
        display={{base: 'none', md: 'flex'}}
      >
        <Flex
          w='100%'
          direction={{base: 'column', '2xl': 'row'}}
          gap={2}
        >
          <HStack
            flex='1'
            flexWrap='wrap'
            spacing={4}
            w='100%'
          >
            {/* Première ligne avec code produit, drapeau et informations de l'article */}
            <HStack
              flex='2'
              minW='300px'
              overflow='hidden'
            >
              <VStack
                minW={0}
                w='100%'
              >
                <ArticleBlock
                  code_article={articleState.data.tolede.code_article}
                  libelles_tronques
                  afficherTagCadencier
                />
              </VStack>
            </HStack>
            <HStack
              alignItems='start'
              alignSelf='center'
              ml='auto'
              flex='1'
              flexGrow={1}
            >
              {/* Bloc de prix */}
              {prixBlock}
              <Box
                mt={{base: '0', md: '15px'}}
                ml='0'
                mr={{base: '0px', md: '5px', xl: '10px'}}
                minW='fit-content'
              >
                {quantiteSaisonniereBlock}
              </Box>
              <VStack
                alignItems='flex-start'
                mt={{base: '0', md: '15px'}}
                ml='auto'
              >
                {ajoutButton}
                <HStack
                  justifyContent='left'
                  flexWrap='nowrap'
                  whiteSpace='nowrap'
                  gap='1'
                >
                  <HistoriqueAchatButton itemState={itemState} />
                </HStack>
              </VStack>
            </HStack>
          </HStack>
        </Flex>
      </VStack>
      {/* Mobile */}
      <VStack
        alignItems='stretch'
        w='100%'
        position='relative'
        display={{base: 'flex', md: 'none'}}
      >
        <HStack>
          <ArticleBlock
            code_article={articleState.data.tolede.code_article}
            libelles_tronques
            afficherTagCadencier
          />
        </HStack>
        <HStack
          justify='space-between'
          w='100%'
          mb='2'
        >
          {prixBlock}
          {quantiteSaisonniereBlock}
        </HStack>
        <HStack w='100%'>{ajoutButton}</HStack>
        <HStack
          w='100%'
          justifyContent='left'
        >
          <HistoriqueAchatButton itemState={itemState} />
        </HStack>
      </VStack>
    </LigneProduitLayout>
  );
});

/**
 * Sous-composant isolé pour le mode panier.
 * Extrait pour éviter que useBuyingBlocks (et ses souscriptions) ne s'exécute
 * sur les lignes en mode cadencier/liste où il n'est pas utilisé.
 */
const ProduitSaisonnierItemPanier: React.FC<{
  itemState: ItemCatalogue2StateFF;
  articleState: State<ArticleDesc>;
  prixHT: number;
  prixTtc?: number;
  tauxTVA: number | undefined;
}> = React.memo(({itemState, articleState, prixHT, prixTtc, tauxTVA}) => {
  const {lignesDePanier} = useListItems();
  const textToastBuy = useSimpleTextToast();
  const ligneDePanier = lignesDePanier?.find(
    (l) => l.data.code_article === itemState.data.code,
  );
  const {uniteQuantiteBlock, buyBlock, commandeEnColis} = useBuyingBlocks(
    textToastBuy,
    'liste',
    itemState,
    ligneDePanier,
  );
  const {
    ajouterAvecPrixSaisi,
    isAddingAvecPrix,
    saisieAvantAjout,
    codeArticle,
  } = useAjouterAvecPrixSaisi(itemState, commandeEnColis);

  // Hors panier : la catégorie vient du catalogue, pas de la ligne. Celle-ci
  // n'est passée que pour une info : un prix saisi ou bloqué par le commercial,
  // qui prend alors la place du tag de catégorie.
  const {categorieDeTarif, modeTarifaireSouhaite} = usePrixCategorieTag(
    ligneDePanier,
    itemState.data.tolede.prix_par_livraison?.[0]?.categorie,
    {contexte: 'catalogue'},
  );
  const typePrix = itemState.data.tolede.prix_par_livraison?.[0]?.type;
  // Même règle que le bloc prix : promo et marché décalent la ligne vers le haut.
  const hasTag =
    !!(categorieDeTarif ?? modeTarifaireSouhaite) ||
    typePrix === 'promo' ||
    typePrix === 'marche';
  const {prixDraft, onEditionPrixChange} = useEditionPrixState();
  const prixBlock = (
    <PrixBlockUnitaire
      prixUnitaire={prixHT}
      prixUnitaireTtc={prixTtc}
      uniteFacturation={itemState.data.unite_de_facturation}
      tauxTVA={tauxTVA}
      style={{h: '100%', mt: {base: '0', md: hasTag ? '-12px' : '15px'}}}
      categorieDeTarif={categorieDeTarif}
      modeTarifaireSouhaite={modeTarifaireSouhaite}
      ligneDePanier={ligneDePanier}
      typePrix={typePrix}
      saisieAvantAjout={saisieAvantAjout}
      ajouterAvecPrixSaisi={ajouterAvecPrixSaisi}
      codeArticle={codeArticle}
      onEditionPrixChange={onEditionPrixChange}
    />
  );

  const articleBlock = (
    <ArticleBlock
      code_article={articleState.data.tolede.code_article}
      libelles_tronques
      ligneDePanier={ligneDePanier}
      afficherTagCadencier
      prixDraft={prixDraft}
    />
  );

  return (
    <LigneProduitCommune
      itemState={itemState}
      articleBlock={articleBlock}
      prixBlock={prixBlock}
      uniteQuantiteBlock={uniteQuantiteBlock}
      aPeser={articleState.data.tolede.a_peser}
      buyBlock={buyBlock}
      somethingSubmitting={isAddingAvecPrix}
      ligneDePanier={ligneDePanier}
      afficher_suggestions
    />
  );
});
